import React from 'react'

import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { assets as uiAssets } from '@/ui/assets'
import { FormControl } from '@/ui/atoms/form/Form'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/ui/atoms/select/Select'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { useChainId } from 'wagmi'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import Image from 'next/image'
import { truncateWithEllipsis } from '@/domain/common/format'
import { L1Badge } from '../l1-badge/L1Badge'

interface AssetSelectorProps {
  assets: { token: Token; balance?: NormalizedUnitNumber; l1Balance?: NormalizedUnitNumber }[]
  selectedAsset?: Token
  setSelectedAsset?: (newSymbol: TokenSymbol) => void
  withFormControl?: boolean
  disabled?: boolean
  open?: boolean
  resetBorrowStatus?: () => void
  isL1?: boolean
  swapMode?: string
  isDestination?: boolean
}

export function AssetSelector({
  assets,
  selectedAsset,
  setSelectedAsset,
  withFormControl,
  disabled,
  open,
  resetBorrowStatus,
  isL1,
  swapMode,
  isDestination,
}: AssetSelectorProps) {
  const Wrapper = withFormControl ? FormControl : React.Fragment
  const readonly = assets.length <= 1
  const chainId = useChainId()
  const displayToken = selectedAsset ? getDisplayToken(chainId, selectedAsset, { skipWrappedNative: true }) : undefined

  return (
    <Select
      disabled={disabled}
      open={open}
      value={selectedAsset?.symbol}
      onValueChange={(value) => {
        if (disabled && resetBorrowStatus) {
          resetBorrowStatus()
        }
        if (value) {
          setSelectedAsset?.(TokenSymbol(value))
        }
      }}
    >
      <SelectTrigger
        disabled={(disabled && !resetBorrowStatus) || readonly}
        className={cn('h-14 w-36', readonly && 'disabled:cursor-default disabled:opacity-100')}
        data-testid={testIds.component.AssetSelector.trigger}
        aria-label="Asset selector"
        hideIcon={readonly}
      >
        <Wrapper>
          <div className="flex flex-row items-center gap-2">
            {selectedAsset && (
              <div className="relative">
                <TokenIcon token={selectedAsset} className="h-6 w-6" />
                {isL1 && <L1Badge />}
              </div>
            )}
            <Typography className="whitespace-nowrap text-nowrap text-foreground">
              <span title={displayToken?.symbol ?? ''}>{truncateWithEllipsis(displayToken?.symbol ?? '', 8)}</span>
            </Typography>
          </div>
        </Wrapper>
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {assets.map((a) => {
          const displayToken = getDisplayToken(chainId, a.token, { skipWrappedNative: true })
          const isBridgeToHyper = swapMode === 'bridgeToHyperEVM'
          const isDest = isDestination ?? false
          const balanceToShow = isBridgeToHyper === isDest ? a.balance : a.l1Balance

          return (
            <SelectItem key={a.token.symbol} value={a.token.symbol}>
              <div className={cn('flex flex-row justify-between', balanceToShow && 'w-40')}>
                <div className="flex flex-row gap-2" data-testid={testIds.component.AssetSelector.option}>
                  <TokenIcon token={displayToken} className="h-6 w-6" />
                  <Typography className="text-muted-foreground">{displayToken.symbol}</Typography>
                </div>
                {balanceToShow && (
                  <div className="flex flex-row items-center">
                    <Typography variant="prompt">{a.token.format(balanceToShow, { style: 'compact' })}</Typography>
                    {isL1 && <Image className="ml-2" src={uiAssets.wallet} alt="wallet" />}
                  </div>
                )}
              </div>
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}
